// eslint-disable-next-line no-restricted-imports
import {
  UpdateLegalClientByAdminDto_Input,
  MutationInput_LegalClientController_UpdateById_Input_RiskLevel,
  MutationInput_LegalClientController_UpdateById_Input_InformationUpdateFrequency,
  MutationInput_LegalClientController_UpdateById_Input_Tariff,
  MutationInput_LegalClientController_UpdateById_Input_Type,
  MutationInput_LegalClientController_UpdateById_Input_Classification,
} from 'api/generated/graphql';
import { UpdateLegalClientParams } from 'api/types/params';
import { formatToISODate } from 'helpers';

import {
  parseCurrencyParam,
  parseDebtsParam,
  parseInformationUpdateFrequency,
  parseRiskLevel,
} from '../client/parseParam';
import { parseManagementTypeParam } from '../parseParam';

import {
  parseFinancialMonitoringParam,
  parseLegalClientBusinessRelationsParam,
  parseLegalClientFinancingSourcesParam,
  parseLegalClientOrderSubmissionContactsParam,
  parseOrganizationalAndLegalFormParam,
  parseCategoryTypeParam,
  parseEconomicSectorTypeParam,
  parseConstitutionalDocumentParam,
  parseLegalClientOpenAccountOrderParam,
  parseLegalClientActivityParam,
  parseLegalClientKnowledgeAndExperienceParam,
  parseLegalClientServiceContractParam,
} from './parseParam';
import { parseLegalClientInvestmentPurposeAndNatureParam } from './parseParam/parseLegalClientInvestmentPurposeAndNatureParam';
import { parseLegalClientSaveSourcesOfIncomeParam } from './parseParam/parseLegalClientSaveSourcesOfIncomeParam';

export const parseUpdateLegalClientParams = (
  params: UpdateLegalClientParams
): UpdateLegalClientByAdminDto_Input => ({
  versionNumber: params.versionNumber,
  reason: params.reason,
  fullName: params.fullName,
  shortName: params.shortName,
  fullNameEng: params.fullNameEng,
  shortNameEng: params.shortNameEng,
  debts: params.debts ? parseDebtsParam(params.debts) : undefined,
  bankAccounts: params.bankAccounts,

  financingSources: params.financingSources
    ? parseLegalClientFinancingSourcesParam(params.financingSources)
    : undefined,
  legalAddress: {
    country: params.legalAddress.country,
    house: params.legalAddress.house,
    locality: params.legalAddress.locality,
    postalCode: params.legalAddress.postalCode,
    region: params.legalAddress.region,
    street: params.legalAddress.street,
    apartment: params.legalAddress.apartment,
    building: params.legalAddress.building,
    district: params.legalAddress.district,
    confirmationFileIds: params.legalAddress.confirmationFileIds,
  },
  actualAddress: params.actualAddress,

  taxResidences: params.taxResidences,

  orderSubmissionContacts: parseLegalClientOrderSubmissionContactsParam(
    params.orderSubmissionContacts
  ),

  withdrawalAccounts: params.withdrawalAccounts.map((p) => ({
    ...p,
    currency: parseCurrencyParam(p.currency),
  })),

  accreditedInvestorStatus: params.accreditedInvestorStatus,

  riskLevel:
    parseRiskLevel<MutationInput_LegalClientController_UpdateById_Input_RiskLevel>(
      params.riskLevel
    ),
  informationUpdateFrequency:
    parseInformationUpdateFrequency<MutationInput_LegalClientController_UpdateById_Input_InformationUpdateFrequency>(
      params.informationUpdateFrequency
    ),
  riskLevelReviewDate: params.riskLevelReviewDate
    ? formatToISODate(params.riskLevelReviewDate)
    : null,
  lastInformationUpdateDate: params.lastInformationUpdateDate
    ? formatToISODate(params.lastInformationUpdateDate)
    : null,
  lastOperationMonitoringResults: params.lastOperationMonitoringResults ?? null,
  usedServices: params.usedServices ?? null,

  code: params.code,
  centralDepositoryAccountNumber: params.centralDepositoryAccountNumber,
  centralDepositoryAccountNumberOpeningDate:
    params.centralDepositoryAccountNumberOpeningDate
      ? formatToISODate(params.centralDepositoryAccountNumberOpeningDate)
      : null,
  personalAccountNumber: params.personalAccountNumber,
  personalAccountOpeningDate: params.personalAccountOpeningDate
    ? formatToISODate(params.personalAccountOpeningDate)
    : params.personalAccountOpeningDate,
  personalAccountClosingDate: params.personalAccountClosingDate
    ? formatToISODate(params.personalAccountClosingDate)
    : null,
  personalAccountFreezingDate: params.personalAccountFreezingDate
    ? formatToISODate(params.personalAccountFreezingDate)
    : null,
  managerId: params.managerId,

  additionalInformationFileIds: params.additionalInformationFileIds,
  additionalDocumentFileIds: params.additionalDocumentFileIds,

  isLicensed: params.license.isLicensed,
  activityTypes: params.activityTypes,
  OKPOCode: params.OKPOCode,

  licenses: params.license.licenses,
  businessRelationship: params.businessRelationship
    ? parseLegalClientBusinessRelationsParam(params.businessRelationship)
    : undefined,

  stateRegistration: params.stateRegistration,
  constitutionalDocument: parseConstitutionalDocumentParam(
    params.constitutionalDocument
  ),

  contacts: params.contacts,
  financialMonitoring: parseFinancialMonitoringParam(
    params.financialMonitoring
  ),
  organizationalAndLegalForm: parseOrganizationalAndLegalFormParam(
    params.organizationalAndLegalForm
  ),
  registrationPlace: params.registrationPlace,

  openAccountOrder_Finance: params.openAccountOrder_Finance
    ? parseLegalClientOpenAccountOrderParam(params.openAccountOrder_Finance)
    : undefined,

  category: parseCategoryTypeParam(params.category),
  economicSector: parseEconomicSectorTypeParam(params.economicSector),
  type: params.clientType
    ? MutationInput_LegalClientController_UpdateById_Input_Type[
        params.clientType
      ]
    : null,
  classification: params.classification
    ? MutationInput_LegalClientController_UpdateById_Input_Classification[
        params.classification
      ]
    : null,
  isInsider: params.isInsider,
  managementType: parseManagementTypeParam(params.managementType),
  isFinancialMonitoringSubject: params.isFinancialMonitoringSubject,
  isKazakhstanResident: params.isKazakhstanResident,
  taxBenefits: params.taxBenefits,

  serviceContract: parseLegalClientServiceContractParam(params.serviceContract),

  tariff: params.tariff
    ? MutationInput_LegalClientController_UpdateById_Input_Tariff[params.tariff]
    : undefined,

  invoice: params.invoice
    ? {
        fileId: params.invoice.fileId ?? null,
        paymentConfirmationFileIds:
          params.invoice.paymentConfirmationFileIds ?? null,
      }
    : undefined,
  ownershipStructureFileIds: params.ownershipStructureFileIds,
  signaturesCardFileIds: params.signaturesCardFileIds,
  signedQuestionnaireFileIds: params.signedQuestionnaireFileIds,
  questionnaireFileId: params.questionnaireFileId,
  identificationNumbers: params.identificationNumbers
    ? {
        giin: params.identificationNumbers.GIIN ?? null,
        lei: params.identificationNumbers.LEI ?? null,
      }
    : undefined,

  activity: params.activity
    ? parseLegalClientActivityParam(params.activity)
    : undefined,

  knowledgeAndExperience: params.knowledgeAndExperience
    ? parseLegalClientKnowledgeAndExperienceParam(params.knowledgeAndExperience)
    : undefined,

  targetedFinancialInstruments: params.targetedFinancialInstruments,

  investmentPurposeAndNature: params.investmentPurposeAndNature
    ? parseLegalClientInvestmentPurposeAndNatureParam(
        params.investmentPurposeAndNature
      )
    : undefined,

  incomeSources: params.sourcesOfIncome
    ? parseLegalClientSaveSourcesOfIncomeParam(params.sourcesOfIncome)
    : undefined,

  W8BENForm: params.W8BENForm,

  blankClientServiceAgreementFileId_Global:
    params.blankClientServiceAgreementFileId_Global,
  companySignedNoticeOfInvestmentAccountOpeningFileId:
    params.companySignedNoticeOfInvestmentAccountOpeningFileId,
  fullySignedClientServiceAgreementFileId_Global:
    params.fullySignedClientServiceAgreementFileId_Global,
  companySignedClientServiceAgreementFileId_Global:
    params.companySignedClientServiceAgreementFileId_Global,
});
